import { Action, ActionType, ExternalLinkActionDetail, GuidedOnboardingService, TrailheadActionDetail, VideoActionDetail } from '@tableau/guided-onboarding';
import { LinkDiagonalIcon } from '@tableau/icons/LinkDiagonalIcon';
import { NotificationCheckIcon } from '@tableau/icons/NotificationCheckIcon';
import { SignpostIcon } from '@tableau/icons/SignpostIcon';
import { TrailheadIcon } from '@tableau/icons/TrailheadIcon';
import classNames from 'classnames';
import { sanitize } from 'dompurify';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { useEventCallback } from '~src/commonInfra/services/VPCEvent';
import { useTitleForOverflowContent } from '~src/spacesPlatform/hooks/useTitleForOverflowContent';
import { sendUsageEvent } from '~src/spacesPlatform/hooks/useUsage';

import { GuidedOnboardingBannerService, WelcomeBannerCardFeatureName, WelcomeBannerClickType, WelcomeBannerFeatureName } from '../services/GuidedOnboardingBannerService';
import { GuidedOnboardingStyles } from '../styles/GuidedOnboardingStyles';
import { GuidedOnboardingTestIds } from './GuidedOnboardingWelcomeBanner';

export type GuidedOnboardingBannerCardTelemetryData = {
    actionType: ActionType;
    cardIndex: string;
    completed: string;
}; // All the property types need to be string since this data will be stored in database as JSON string but the database can only deserialize the JSON string to objects with same property type.

const getCardIcon = (type: ActionType): JSX.Element => {
    switch (type) {
        case 'walkThrough':
            return <SignpostIcon size={24} />;
        case 'externalLink':
            return <LinkDiagonalIcon size={18} />;
        case 'trailhead':
            return <TrailheadIcon size={24} />;
        default:
            return null;
    }
};

const getCardActionText = (type: ActionType): string => {
    switch (type) {
        case 'walkThrough':
            return Messages.guidedOnboardingBannerCardActionGuide();
        case 'externalLink':
            return Messages.guidedOnboardingBannerCardActionResource();
        case 'trailhead':
            return Messages.guidedOnboardingBannerCardActionTrailhead();
        default:
            return '';
    }
};

const loadVidyardScript = (): void => {
    if (!document.getElementById('vidyard-script')) {
        const vidyardScript = document.createElement('script');
        vidyardScript.id = 'vidyard-script';
        vidyardScript.type = 'text/javascript';
        vidyardScript.async = true;
        vidyardScript.src = 'https://play.vidyard.com/embed/v4.js';
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode.insertBefore(vidyardScript, firstScript);
    }
};

type Props = Action & { isCompleted?: boolean, onCardClick?: () => void };

function renderCheckmark(className: string) {
    return (
        <span className={className} title={Messages.guidedOnboardingCardCompletedCheckmark()} data-tb-test-id={GuidedOnboardingTestIds.onboardingCardCheckmark}>
            <NotificationCheckIcon size={12} color="#fff" alt={Messages.guidedOnboardingCardCompletedCheckmark()} />
        </span>
    );
}

function getTelemetryDataString(data: GuidedOnboardingBannerCardTelemetryData) {
    return JSON.stringify(data);
}

function getCardIndex(cardElement: HTMLElement) {
    let cardIndex = -1;
    for (let i = 0; i < cardElement.parentElement.children.length; i++) {
        if (cardElement.parentElement.children.item(i) === cardElement) {
            cardIndex = i;
            break;
        }
    }
    return cardIndex;
}

export function GuidedOnboardingBannerCard(props: Props): JSX.Element {
    const iconClass = getCardIcon(props.type);
    const actionText = getCardActionText(props.type);
    const cardElementRef = React.useRef<HTMLDivElement>(null);
    // Local state for immediate checkmark update
    const [completed, setCompleted] = React.useState(props.isCompleted);
    const impressionTimestamp = React.useRef<Date>(null);
    const contentMainAreaContainerRef = React.useRef<HTMLDivElement>(null);

    useTitleForOverflowContent(contentMainAreaContainerRef);

    useEventCallback(GuidedOnboardingBannerService.instance.bannerLayoutChanged, () => {
        setTimeout(() => {
            if (!impressionTimestamp.current && cardElementRef.current) {
                const cardRect = cardElementRef.current.getBoundingClientRect();
                const containerRect = cardElementRef.current.parentElement.getBoundingClientRect();

                if (cardRect.left >= containerRect.left && cardRect.right <= containerRect.right) {
                    impressionTimestamp.current = new Date();
                    sendUsageEvent(WelcomeBannerCardFeatureName, 'impression', {
                        a: props.id,
                        b: getTelemetryDataString({
                            actionType: props.type,
                            cardIndex: getCardIndex(cardElementRef.current).toString(),
                            completed: completed ? 'yes' : 'no'
                        })
                    });
                }
            }
        }, 1000); // Wait for 1 second since when scrolling happens in filmstrip, it takes a little bit time to actually scroll the card into the viewport.
    });

    React.useEffect(() => {
        setCompleted(props.isCompleted);
    }, [props.isCompleted]);

    React.useEffect(() => {
        if (props.type === 'video') {
            loadVidyardScript();
        }
    }, [props.type]);

    const handleGuideClick = () => {
        GuidedOnboardingService.instance.startWalkThroughAction(props.id);
    };

    const handleVideoClick = () => {
        // TODO: Implement video playback functionality
    };

    const handleExternalLinkClick = async () => {
        window.open((props.detail as ExternalLinkActionDetail | TrailheadActionDetail).link, '_blank');
        await GuidedOnboardingBannerService.instance.setOnboardingActionAsComplete(props.id);
        setCompleted(true); // Show checkmark immediately
    };

    const handleCardClick = () => {
        switch (props.type) {
            case 'walkThrough':
                handleGuideClick();
                break;
            case 'video':
                handleVideoClick();
                break;
            case 'externalLink':
            case 'trailhead':
                handleExternalLinkClick();
                break;
            default:
                break;
        }

        if (impressionTimestamp.current) {
            sendUsageEvent(WelcomeBannerCardFeatureName, 'click_through', {
                a: props.id,
                b: getTelemetryDataString({
                    actionType: props.type,
                    cardIndex: getCardIndex(cardElementRef.current).toString(),
                    completed: completed ? 'yes' : 'no'
                }),
                c: (new Date()).getTime() - impressionTimestamp.current.getTime()
            });
        }
        sendUsageEvent(WelcomeBannerFeatureName, 'click_through', {
            a: WelcomeBannerClickType[WelcomeBannerClickType.card]
        });

        // Call the onCardClick callback if provided (e.g., to close modal)
        if (props.onCardClick) {
            props.onCardClick();
        }
    };

    const renderMainContent = () => {
        if (props.type === 'video') {
            // TODO: Final styling of the video card is pending - current video has watermark
            return <div data-tb-test-id={GuidedOnboardingTestIds.videoContent} dangerouslySetInnerHTML={{ __html: sanitize((props.detail as VideoActionDetail).embeddingCode) }} />;
        }
        return (
            <>
                <h3 data-tb-test-id={GuidedOnboardingTestIds.cardTitle}>{props.name()}</h3>
                <p data-tb-test-id={GuidedOnboardingTestIds.cardDescription} className={GuidedOnboardingStyles.cardDescription}>{props.description()}</p>
            </>
        );
    };

    const renderFooterContent = () => {
        if (props.type === 'video') {
            return (
                <>
                    <h3 data-tb-test-id={GuidedOnboardingTestIds.cardTitle}>{props.name()}</h3>
                    {!completed && (
                        <span data-tb-test-id={GuidedOnboardingTestIds.cardLength}>{props.length()}</span>
                    )}
                </>
            );
        }
        return (
            <>
                <div className={GuidedOnboardingStyles.cardType}>
                    {iconClass}
                    <span data-tb-test-id={GuidedOnboardingTestIds.cardType}>{actionText}</span>
                </div>
                {!completed && (
                    <span data-tb-test-id={GuidedOnboardingTestIds.cardLength}>{props.length()}</span>
                )}
            </>
        );
    };

    return (
        <div
            ref={cardElementRef}
            className={GuidedOnboardingStyles.cardWrapper}
            data-tb-test-id={GuidedOnboardingTestIds.bannerCard}
            onClick={handleCardClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick();
                }
            }}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            aria-label={[
                props.name(),
                props.description(),
                actionText,
                completed
                    ? Messages.guidedOnboardingCardCompletedCheckmark()
                    : (rawLength => rawLength && /[~～]/.test(rawLength)
                        ? Messages.guidedOnboardingApproximateDuration({ DURATION: rawLength.replace(/[~～]/, '').trim() })
                        : rawLength)(props.length())
            ].filter(Boolean).join(' - ')}
        >
            <div
                className={classNames(
                    GuidedOnboardingStyles.cardContainer,
                    { [GuidedOnboardingStyles.cardContainerCompleted]: completed }
                )}
                style={{ position: 'relative' }}
            >
                {completed && renderCheckmark(GuidedOnboardingStyles.cardCheckmarkCircle)}
                <div className={GuidedOnboardingStyles.cardContent}>
                    <div className={GuidedOnboardingStyles.cardMain}>
                        <div ref={contentMainAreaContainerRef}>
                            {renderMainContent()}
                        </div>
                    </div>
                    <div className={GuidedOnboardingStyles.cardFooter}>
                        <div className={GuidedOnboardingStyles.cardTypeContainer}>
                            {renderFooterContent()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GuidedOnboardingBannerCard;

